import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CaretDown, ArrowUpRight } from '@phosphor-icons/react';
import '../styles/aurablox-navigation.css';

const NAV_ITEMS = [
  {
    id: 'solutions', path: '/solutions', label: 'The engagement',
    sections: [
      { title: 'Commercial data foundation', links: [
        { to: '/solutions#outcome-heading', label: 'The commercial outcome', desc: 'Consistent entities, traceable reporting, and clear ownership' },
        { to: '/solutions#delivery-heading', label: 'Four delivery stages', desc: 'Blueprint, lakehouse, reconciliation, and activation' },
      ] },
      { title: 'Readiness and acceptance', links: [
        { to: '/solutions#measures-heading', label: 'Acceptance targets', desc: 'Agree baselines and verification before the build' },
        { to: '/solutions#fit-heading', label: 'Who this fits', desc: 'Deadline, funded platform, and an authorized sponsor' },
        { to: '/solutions#assurance-heading', label: 'Milestone assurance', desc: 'Fees and remedies tied to the signed acceptance criteria' },
      ] },
    ],
    featured: { eyebrow: 'THE 90-DAY ENGAGEMENT', title: 'One commercial data foundation', desc: 'A focused Databricks engagement from diagnostic to operational handover.', img: '/images/aurablox-audit.jpg', to: '/solutions', cta: 'Explore the engagement ↗' },
  },
  {
    id: 'training', path: '/training', label: 'Enablement',
    sections: [
      { title: 'Operational handover', links: [
        { to: '/training#engagement', label: 'Ready to operate', desc: 'Runbooks, controls, exceptions, and reviewed changes' },
      ] },
      { title: 'Supplementary references', links: [
        { to: '/training#primer', label: 'AI engineering concepts', desc: 'Models, context, tools, and review practices' },
        { to: '/training#architectures', label: 'Reference library', desc: 'Optional diagrams with readable technical notes' },
      ] },
    ],
    featured: { eyebrow: 'OWN THE FOUNDATION', title: 'A team ready to run it', desc: 'Operational enablement is part of the final delivery stage.', img: '/images/training/safe-loop-governance.svg', to: '/training', cta: 'Explore handover ↗' },
  },
  {
    id: 'work', path: '/technology', label: 'Our work',
    sections: [
      { title: 'Public engineering work', links: [
        { to: '/technology#proofs', label: 'Products and experiments', desc: 'Independent tools and product examples you can inspect' },
        { to: '/technology#sprout-demo', label: 'Sprout walkthrough', desc: 'A source-backed developer-tool install flow' },
      ] },
      { title: 'Technical background', links: [
        { to: '/technology#stack', label: 'Architecture experience', desc: 'Supporting experience across data, platforms, and tools' },
        { to: '/technology#tooling-heading', label: 'Engineering tools', desc: 'Tools selected around the requirements of each system' },
      ] },
    ],
    featured: { eyebrow: 'PUBLIC EXAMPLE', title: 'Sprout developer CLI', desc: 'A separate open-source tool for local developer environments.', img: '/images/projects/sprout-website-hero.webp', to: '/technology', cta: 'Inspect the work ↗' },
  },
  {
    id: 'about', path: '/about', label: 'About',
    sections: [
      { title: 'The team', links: [ { to: '/about#leadership', label: 'Leadership', desc: 'The people responsible for the engagement' } ] },
      { title: 'How we operate', links: [ { to: '/about#model', label: 'Working principles', desc: 'Defined scope, reviewed delivery, and human ownership' } ] },
    ],
    featured: { eyebrow: 'COMMERCIAL DATA SPECIALISTS', title: 'Clear ownership', desc: 'Meet the team behind the commercial data foundation engagement.', img: '/images/aurablox-systems.jpg', to: '/about', cta: 'Meet the team ↗' },
  },
];

const Navbar = () => {
  const { pathname, hash } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const navRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const menuId = 'aurablox-primary-menu';

  useEffect(() => {
    setMenuOpen(false);
    setActiveDropdown(null);
    setMobileExpanded(null);
  }, [pathname, hash]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setActiveDropdown(null);
        if (menuOpen) {
          setMenuOpen(false);
          toggleRef.current?.focus();
        }
      }
    };
    const onPointerDown = (event) => {
      if (!navRef.current?.contains(event.target)) {
        setMenuOpen(false);
        setActiveDropdown(null);
      }
    };
    const onResize = () => {
      if (window.matchMedia('(min-width: 961px)').matches) {
        setMenuOpen(false);
      } else {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('resize', onResize);
    };
  }, [menuOpen]);

  const clearCloseTimer = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMouseEnter = (id) => {
    if (window.matchMedia('(max-width: 960px)').matches) return;
    clearCloseTimer();
    setActiveDropdown(id);
  };

  const handleMouseLeave = () => {
    if (window.matchMedia('(max-width: 960px)').matches) return;
    clearCloseTimer();
    closeTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 140);
  };

  const closeAll = () => {
    clearCloseTimer();
    setMenuOpen(false);
    setActiveDropdown(null);
    setMobileExpanded(null);
  };

  const toggleMobileSection = (id) => {
    setMobileExpanded((prev) => (prev === id ? null : id));
  };

  const activeItem = NAV_ITEMS.find((item) => item.id === activeDropdown);

  return (
    <header
      className={`ab-nav-shell${activeItem ? ' has-mega-open' : ''}`}
      ref={navRef}
      onMouseLeave={handleMouseLeave}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setActiveDropdown(null);
        }
      }}
    >
      <nav className="ab-nav" aria-label="Primary">
        <Link to="/" className="ab-nav__brand" aria-label="AURABLOX home" onClick={closeAll}>
          <span className="ab-nav__seal" aria-hidden="true" />
          <span>AURABLOX</span>
        </Link>

        <button
          ref={toggleRef}
          className="ab-nav__toggle"
          type="button"
          aria-expanded={menuOpen}
          aria-controls={menuId}
          aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span aria-hidden="true">{menuOpen ? '× Close' : 'Menu'}</span>
        </button>

        <div
          className={`ab-nav__menu${menuOpen ? ' is-open' : ''}`}
          id={menuId}
          ref={menuRef}
        >
          <ul className="ab-nav__list">
            {NAV_ITEMS.map((item) => {
              const current =
                pathname === item.path ||
                (item.path !== '/' && pathname.startsWith(`${item.path}/`));
              const isOpen = activeDropdown === item.id;
              const isMobileOpen = mobileExpanded === item.id;

              return (
                <li
                  key={item.id}
                  className={`ab-nav__item${isOpen ? ' is-open' : ''}${isMobileOpen ? ' is-mobile-open' : ''}`}
                  onMouseEnter={() => handleMouseEnter(item.id)}
                >
                  <div className="ab-nav__item-row">
                    <Link
                      to={item.path}
                      className={`ab-nav__link${current ? ' is-current' : ''}`}
                      aria-current={current ? 'page' : undefined}
                      onFocus={() => handleMouseEnter(item.id)}
                      onClick={closeAll}
                    >
                      <span>{item.label}</span>
                      <CaretDown
                        size={12}
                        weight="bold"
                        className={`ab-nav__caret${isOpen ? ' is-open' : ''}`}
                        aria-hidden="true"
                      />
                    </Link>
                    <button
                      type="button"
                      className="ab-nav__mobile-expand"
                      aria-expanded={isMobileOpen}
                      aria-label={`Toggle ${item.label} sub-navigation`}
                      onClick={() => toggleMobileSection(item.id)}
                    >
                      <CaretDown
                        size={14}
                        weight="bold"
                        className={isMobileOpen ? 'is-open' : ''}
                        aria-hidden="true"
                      />
                    </button>
                  </div>

                  {/* Mobile Accordion Submenu */}
                  <div className={`ab-nav__mobile-sub${isMobileOpen ? ' is-open' : ''}`}>
                    {item.sections.map((sec) => (
                      <div key={sec.title} className="ab-nav__mobile-group">
                        <span className="ab-nav__mobile-group-title">{sec.title}</span>
                        <ul>
                          {sec.links.map((lnk) => (
                            <li key={lnk.label}>
                              <Link to={lnk.to} onClick={closeAll}>
                                <span className="ab-nav__mobile-link-label">{lnk.label}</span>
                                <span className="ab-nav__mobile-link-desc">{lnk.desc}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="ab-nav__cta-group">
            <Link
              to="/contact#inquiry"
              className={`ab-nav__cta-btn${pathname === '/contact' ? ' is-current' : ''}`}
              onClick={closeAll}
            >
              <span>Contact us</span>
              <ArrowUpRight size={13} weight="bold" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </nav>

      {/* ─── DESKTOP CONSULTING MEGA-MENU DROPDOWN ─── */}
      {activeItem && (
        <div
          className="ab-megamenu"
          role="region"
          aria-label={`${activeItem.label} navigation directory`}
          onMouseEnter={clearCloseTimer}
          onMouseLeave={handleMouseLeave}
        >
          <div className="ab-megamenu__inner">
            {/* Columns 1 & 2: Sub-clicks & Page Links */}
            {activeItem.sections.map((section) => (
              <div key={section.title} className="ab-megamenu__col">
                <div className="ab-megamenu__section-title">
                  <span className="ab-megamenu__dot" aria-hidden="true" />
                  <span>{section.title}</span>
                </div>
                <ul className="ab-megamenu__list">
                  {section.links.map((lnk) => (
                    <li key={lnk.label}>
                      <Link to={lnk.to} className="ab-megamenu__link" onClick={closeAll}>
                        <span className="ab-megamenu__link-title">
                          {lnk.label}
                          <ArrowUpRight size={13} weight="bold" className="ab-megamenu__link-arrow" aria-hidden="true" />
                        </span>
                        <span className="ab-megamenu__link-desc">{lnk.desc}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Column 3: Featured Spotlight + Quick Page Directory */}
            <div className="ab-megamenu__col ab-megamenu__col--featured">
              <div className="ab-megamenu__section-title">
                <span className="ab-megamenu__dot" aria-hidden="true" />
                <span>{activeItem.featured.eyebrow}</span>
              </div>
              <Link to={activeItem.featured.to} className="ab-megamenu__spotlight" onClick={closeAll}>
                <div className="ab-megamenu__spotlight-media">
                  <img src={activeItem.featured.img} alt={activeItem.featured.title} loading="lazy" />
                </div>
                <div className="ab-megamenu__spotlight-body">
                  <h4>{activeItem.featured.title}</h4>
                  <p>{activeItem.featured.desc}</p>
                  <span className="ab-megamenu__spotlight-cta">{activeItem.featured.cta}</span>
                </div>
              </Link>

              <div className="ab-megamenu__quickbar">
                <span className="ab-megamenu__quickbar-label">Explore pages:</span>
                <div className="ab-megamenu__quickbar-links">
                  <Link to="/solutions" onClick={closeAll}>The engagement</Link>
                  <Link to="/training" onClick={closeAll}>Enablement</Link>
                  <Link to="/technology" onClick={closeAll}>Our work</Link>

                  <Link to="/about" onClick={closeAll}>About</Link>


                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
